import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Megaphone,
  Calendar,
  FileText,
  Video,
  Users,
  Bell,
  Clock,
  TrendingUp,
  TrendingDown,
  Activity,
  Plus,
  ArrowRight,
  User,
} from "lucide-react";
import { collaborationService } from "../../services/collaborationService";

const CollaborationDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    recentMessages: [],
    announcements: [],
    upcomingEvents: [],
    recentDocuments: [],
    activeConferences: [],
    forumActivity: [],
    stats: {
      unreadMessages: 0,
      pendingAnnouncements: 0,
      todayEvents: 0,
      sharedDocuments: 0,
      activeConferences: 0,
      forumPosts: 0,
    },
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Load data from multiple endpoints
      const [
        messagesRes,
        announcementsRes,
        eventsRes,
        documentsRes,
        conferencesRes,
        forumsRes,
      ] = await Promise.allSettled([
        collaborationService.getMessageThreads({ limit: 5 }),
        collaborationService.getAnnouncements({ limit: 5 }),
        collaborationService.getCalendarEvents({
          start: new Date().toISOString().split("T")[0],
          end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0],
          limit: 5,
        }),
        collaborationService.getSharedDocuments({ limit: 5 }),
        collaborationService.getVideoConferences({
          status: "scheduled",
          limit: 5,
        }),
        collaborationService.getForums(),
      ]);

      // Process results
      const recentMessages =
        messagesRes.status === "fulfilled"
          ? messagesRes.value.data?.data?.threads || []
          : [];
      const announcements =
        announcementsRes.status === "fulfilled"
          ? announcementsRes.value.data?.data?.announcements || []
          : [];
      const upcomingEvents =
        eventsRes.status === "fulfilled"
          ? eventsRes.value.data?.data?.events || []
          : [];
      const recentDocuments =
        documentsRes.status === "fulfilled"
          ? documentsRes.value.data?.data?.documents || []
          : [];
      const activeConferences =
        conferencesRes.status === "fulfilled"
          ? conferencesRes.value.data?.data?.conferences || []
          : [];
      const forums =
        forumsRes.status === "fulfilled"
          ? forumsRes.value.data?.data?.forums || []
          : [];

      // Calculate stats - Fetch ALL threads for accurate unread count
      let totalUnreadMessages = 0;
      try {
        // Fetch all threads with unread messages
        const allThreadsRes = await collaborationService.getMessageThreads({});
        const allThreads = allThreadsRes.data?.data?.threads || [];

        // Sum up unread_count from each thread (backend adds current user's unread_count to thread object)
        totalUnreadMessages = allThreads.reduce((sum, thread) => {
          return sum + (thread.unread_count || 0);
        }, 0);
      } catch (error) {
        // Fallback to recent messages if full fetch fails
        console.log("Using recent messages for unread count", error);
        totalUnreadMessages = recentMessages.reduce((sum, thread) => {
          return sum + (thread.unread_count || 0);
        }, 0);
      }

      const stats = {
        unreadMessages: totalUnreadMessages,
        pendingAnnouncements: announcements.filter(
          (a) => a.requires_acknowledgment && !a.acknowledged,
        ).length,
        todayEvents: upcomingEvents.filter(
          (e) =>
            new Date(e.start_date).toDateString() === new Date().toDateString(),
        ).length,
        sharedDocuments: recentDocuments.length,
        activeConferences: activeConferences.length,
        forumPosts: forums.reduce(
          (sum, forum) => sum + (forum.post_count || 0),
          0,
        ),
      };

      setDashboardData({
        recentMessages,
        announcements,
        upcomingEvents,
        recentDocuments,
        activeConferences,
        forumActivity: forums.slice(0, 5),
        stats,
      });
    } catch (error) {
      console.error("Load dashboard data error:", error);
      collaborationService.showToast("Failed to load dashboard data", "error");
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    {
      title: "Send Message",
      description: "Start a new conversation",
      icon: MessageSquare,
      color: "bg-blue-500",
      action: () => navigate("/collaboration/messaging"),
    },
    {
      title: "Create Announcement",
      description: "Share important news",
      icon: Megaphone,
      color: "bg-orange-500",
      action: () => navigate("/collaboration/announcements/create"),
    },
    {
      title: "Schedule Event",
      description: "Add calendar event",
      icon: Calendar,
      color: "bg-green-500",
      action: () => navigate("/collaboration/calendar/create"),
    },
    {
      title: "Share Document",
      description: "Upload and share files",
      icon: FileText,
      color: "bg-purple-500",
      action: () => navigate("/collaboration/documents/upload"),
    },
    {
      title: "Start Meeting",
      description: "Create video conference",
      icon: Video,
      color: "bg-red-500",
      action: () => navigate("/collaboration/conferences/create"),
    },
    {
      title: "Join Discussion",
      description: "Participate in forums",
      icon: Users,
      color: "bg-indigo-500",
      action: () => navigate("/collaboration/forums"),
    },
    {
      title: "Manage Groups",
      description: "Create and manage groups",
      icon: Users,
      color: "bg-teal-500",
      action: () => navigate("/collaboration/groups"),
    },
  ];

  const statCards = [
    {
      title: "Unread Messages",
      value: dashboardData.stats.unreadMessages,
      icon: MessageSquare,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      change: "+12%",
      changeType: "increase",
    },
    {
      title: "Pending Acknowledgments",
      value: dashboardData.stats.pendingAnnouncements,
      icon: Bell,
      color: "text-orange-600",
      bgColor: "bg-orange-50",
      change: "-5%",
      changeType: "decrease",
    },
    {
      title: "Today's Events",
      value: dashboardData.stats.todayEvents,
      icon: Calendar,
      color: "text-green-600",
      bgColor: "bg-green-50",
      change: "+8%",
      changeType: "increase",
    },
    {
      title: "Shared Documents",
      value: dashboardData.stats.sharedDocuments,
      icon: FileText,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      change: "+15%",
      changeType: "increase",
    },
    {
      title: "Active Conferences",
      value: dashboardData.stats.activeConferences,
      icon: Video,
      color: "text-red-600",
      bgColor: "bg-red-50",
      change: "+3%",
      changeType: "increase",
    },
    {
      title: "Forum Posts",
      value: dashboardData.stats.forumPosts,
      icon: Users,
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      change: "+22%",
      changeType: "increase",
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Collaboration Hub
          </h1>
          <p className="text-gray-600">
            Connect, communicate, and collaborate with your team
          </p>
        </div>
        <button
          onClick={() => navigate("/collaboration/messaging")}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Quick Start
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((stat, index) => {
          const IconComponent = stat.icon;
          return (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 cursor-pointer transition-all duration-150 hover:shadow-lg"
              style={{ border: '0.5px solid #e5e7eb' }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                  <IconComponent className={`w-6 h-6 ${stat.color}`} />
                </div>
                {stat.change && (
                  <div
                    className={`flex items-center gap-1 text-sm font-medium ${
                      stat.changeType === "increase"
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {stat.changeType === "increase" ? (
                      <TrendingUp size={16} />
                    ) : (
                      <TrendingDown size={16} />
                    )}
                    {stat.change}
                  </div>
                )}
              </div>
              <div className="text-sm text-gray-500 mb-1">{stat.title}</div>
              <div className="text-3xl font-medium text-gray-900">{stat.value}</div>
              {stat.change && (
                <div className="text-xs text-gray-400 mt-2">vs last month</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl" style={{ border: '0.5px solid #e5e7eb' }}>
        <div className="p-6 border-b" style={{ borderColor: '#e5e7eb' }}>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Quick Actions
          </h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickActions.map((action, index) => {
              const IconComponent = action.icon;
              return (
                <div
                  key={index}
                  onClick={action.action}
                  className="flex items-center p-4 rounded-xl hover:bg-gray-50 cursor-pointer transition-all duration-150"
                  style={{ border: '0.5px solid #e5e7eb' }}
                >
                  <div className={`p-3 rounded-xl ${action.color} mr-4 flex-shrink-0`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">
                      {action.title}
                    </h3>
                    <p className="text-sm text-gray-600 truncate">
                      {action.description}
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Messages */}
        <div className="bg-white rounded-2xl" style={{ border: '0.5px solid #e5e7eb' }}>
          <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: '#e5e7eb' }}>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Recent Messages
            </h2>
            <button
              onClick={() => navigate("/collaboration/messaging")}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              View All
            </button>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {dashboardData.recentMessages.length > 0 ? (
                dashboardData.recentMessages.map((thread) => (
                  <div
                    key={thread.id}
                    className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors"
                    onClick={() =>
                      navigate(`/collaboration/messaging/${thread.id}`)
                    }
                  >
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {thread.title || "Direct Message"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {thread.lastMessage?.content || "No messages yet"}
                      </p>
                    </div>
                    {thread.unread_count > 0 && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0">
                        {thread.unread_count}
                      </span>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8 text-sm">
                  No recent messages
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Announcements */}
        <div className="bg-white rounded-2xl" style={{ border: '0.5px solid #e5e7eb' }}>
          <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: '#e5e7eb' }}>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Megaphone className="w-5 h-5" />
              Recent Announcements
            </h2>
            <button
              onClick={() => navigate("/collaboration/announcements")}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              View All
            </button>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {dashboardData.announcements.length > 0 ? (
                dashboardData.announcements.map((announcement) => (
                  <div
                    key={announcement.id}
                    className="p-4 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors"
                    style={{ border: '0.5px solid #e5e7eb' }}
                    onClick={() =>
                      navigate(
                        `/collaboration/announcements/${announcement.id}`,
                      )
                    }
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 mb-1 truncate">
                          {announcement.title}
                        </h4>
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                          {announcement.summary || announcement.content}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              announcement.priority === "high"
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {announcement.priority}
                          </span>
                          <span className="text-xs text-gray-500">
                            {collaborationService.formatRelativeTime(
                              announcement.published_at,
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8 text-sm">
                  No recent announcements
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-2xl" style={{ border: '0.5px solid #e5e7eb' }}>
          <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: '#e5e7eb' }}>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Upcoming Events
            </h2>
            <button
              onClick={() => navigate("/collaboration/calendar")}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              View Calendar
            </button>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {dashboardData.upcomingEvents.length > 0 ? (
                dashboardData.upcomingEvents.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors"
                    onClick={() =>
                      navigate(`/collaboration/calendar/event/${event.id}`)
                    }
                  >
                    <div className="flex-shrink-0">
                      <div
                        className="w-1 h-12 rounded-full"
                        style={{ backgroundColor: event.color || "#3b82f6" }}
                      ></div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {event.title}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Clock className="w-3 h-3 mr-1" />
                        {collaborationService.formatDate(event.start_date)}
                      </div>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 flex-shrink-0">
                      {event.event_type}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8 text-sm">
                  No upcoming events
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Documents */}
        <div className="bg-white rounded-2xl" style={{ border: '0.5px solid #e5e7eb' }}>
          <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: '#e5e7eb' }}>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Recent Documents
            </h2>
            <button
              onClick={() => navigate("/collaboration/documents")}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
            >
              View All
            </button>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {dashboardData.recentDocuments.length > 0 ? (
                dashboardData.recentDocuments.map((document) => (
                  <div
                    key={document.id}
                    className="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors"
                    onClick={() =>
                      navigate(`/collaboration/documents/${document.id}`)
                    }
                  >
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
                        <FileText className="w-5 h-5 text-purple-600" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {document.title}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <span>
                          {collaborationService.formatFileSize(
                            document.file_size,
                          )}
                        </span>
                        <span className="mx-1">•</span>
                        <span>
                          {collaborationService.formatRelativeTime(
                            document.created_at,
                          )}
                        </span>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 flex-shrink-0">
                      {document.document_type}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-8 text-sm">
                  No recent documents
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollaborationDashboard;
